import {useContext, useEffect, useMemo, useState} from 'react';
import {collection, onSnapshot} from 'firebase/firestore';

import {db} from '-/firebase';
import {UserContext} from '-/contexts/User';
import {hasFeature} from '-/features';
import {hasPermission, processRawDocs} from '-/data/utils';
import {resilientSnapshot} from '-/data/errorReporting';
import {isOpen} from '-/pages/onboarding/model';

// Live list of the department's onboarding requests
// (docs/architecture/onboarding-requests.md), plus the two counts the sidebar
// badge and the dashboard card show. `waiting` is what needs the viewer: for a
// department admin, requests Ember Tracking has asked about; for the operator,
// open requests the department touched last.
//
// Subscribes only while the operator has the page switched on and the viewer
// is an admin — the rules deny everyone else, and a listener nobody can see is
// a read for nothing. Recomputed every render, so the switch flipping (the
// settings listener re-renders the menu) subscribes or drops it live.
export default function useOnboardingRequests({active = true} = {}) {
    const {currentUser} = useContext(UserContext);
    const enabled = active && hasFeature('onboardingRequests') && hasPermission(currentUser, 'admin');
    const viewerIsOperator = currentUser?.superadmin === true;
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!enabled) {
            setRequests([]);
            return undefined;
        }
        setLoading(true);
        return resilientSnapshot(
            errorHandler => onSnapshot(collection(db, 'onboardingRequests'), snapshot => {
                setRequests(processRawDocs(snapshot));
                setLoading(false);
            }, errorHandler),
            'onboardingRequests',
            () => setLoading(false)
        );
    }, [enabled]);

    const counts = useMemo(() => {
        const open = requests.filter(isOpen);
        const waiting = viewerIsOperator
            ? open.filter(request => request.lastActivityBy === 'tenant').length
            : open.filter(request => request.status === 'needs_info').length;
        return {open: open.length, waiting};
    }, [requests, viewerIsOperator]);

    return {enabled, requests, loading: enabled && loading, viewerIsOperator, ...counts};
}
